/**
 * Secure temporary storage for authentication credentials
 * Used only during multi-step auth flows (2FA, registration, etc.)
 *
 * Security considerations:
 * - Data is stored in memory only (not localStorage/sessionStorage)
 * - Automatic cleanup on navigation/refresh
 * - Time-limited storage (auto-expire)
 * - Only accessible within current session
 */

interface TempCredentials {
  username: string
  password: string
  token_2fa?: string
  captcha_token?: string
  timestamp: number
  expiresAt: number
}

interface TempRegistrationData {
  username: string
  email: string
  password: string
  timestamp: number
  expiresAt: number
}

// Memory-only storage (cleared on page refresh/navigation)
let tempCredentials: TempCredentials | null = null
let tempRegistrationData: TempRegistrationData | null = null

const CREDENTIALS_TTL = 10 * 60 * 1000 // 10 minutes
const REGISTRATION_TTL = 15 * 60 * 1000 // 15 minutes

/**
 * Composable for secure temporary storage during auth flows
 */
export function useSecureTempStorage() {
  /**
   * Store credentials temporarily for 2FA or CAPTCHA verification
   */
  function storeTempCredentials(username: string, password: string, token_2fa?: string, captcha_token?: string) {
    const now = Date.now()
    tempCredentials = {
      username,
      password,
      token_2fa,
      captcha_token,
      timestamp: now,
      expiresAt: now + CREDENTIALS_TTL
    }
  }

  /**
   * Get temp credentials for 2FA or CAPTCHA verification
   */
  function getTempCredentials(): { username: string; password: string; token_2fa?: string; captcha_token?: string } | null {
    if (!tempCredentials) {
      return null
    }

    // Check if expired
    if (Date.now() > tempCredentials.expiresAt) {
      tempCredentials = null
      return null
    }

    return {
      username: tempCredentials.username,
      password: tempCredentials.password,
      token_2fa: tempCredentials.token_2fa,
      captcha_token: tempCredentials.captcha_token
    }
  }

  /**
   * Clear temp credentials (for security)
   */
  function clearTempCredentials() {
    tempCredentials = null
  }

  /**
   * Store registration data temporarily for email confirmation
   */
  function storeTempRegistrationData(username: string, email: string, password: string) {
    const now = Date.now()
    tempRegistrationData = {
      username,
      email,
      password,
      timestamp: now,
      expiresAt: now + REGISTRATION_TTL
    }
  }

  /**
   * Get temp registration data for email confirmation
   */
  function getTempRegistrationData(): { username: string; email: string; password: string } | null {
    if (!tempRegistrationData) {
      return null
    }

    // Check if expired
    if (Date.now() > tempRegistrationData.expiresAt) {
      tempRegistrationData = null
      return null
    }

    return {
      username: tempRegistrationData.username,
      email: tempRegistrationData.email,
      password: tempRegistrationData.password
    }
  }

  /**
   * Clear temp registration data
   */
  function clearTempRegistrationData() {
    tempRegistrationData = null
  }

  /**
   * Clear all temporary data (on logout, navigation, etc.)
   */
  function clearAllTempData() {
    clearTempCredentials()
    clearTempRegistrationData()
  }

  /**
   * Check if temp credentials are available and valid
   */
  function hasTempCredentials(): boolean {
    return getTempCredentials() !== null
  }

  /**
   * Check if temp registration data is available and valid
   */
  function hasTempRegistrationData(): boolean {
    return getTempRegistrationData() !== null
  }

  return {
    // Credentials for 2FA
    storeTempCredentials,
    getTempCredentials,
    clearTempCredentials,
    hasTempCredentials,

    // Registration data for email confirmation
    storeTempRegistrationData,
    getTempRegistrationData,
    clearTempRegistrationData,
    hasTempRegistrationData,

    // General cleanup
    clearAllTempData
  }
}
